// src/pages/Firefighter.jsx
import WorkspaceInsights from '../components/brain/WorkspaceInsights';
import React, { useMemo, useState, useEffect, useCallback, useRef } from "react";
import { PageHeader, StatGrid, Stat } from "components/ui/page-header";
import { useFitHeight } from "components/ui/compact";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "components/ui/select";
import { RefreshCw, ShieldAlert, Inbox, CloudDownload } from "lucide-react";
import IssueCard, { slackChannelId } from "../components/issues/IssueCard";

// 🔌 added imports
import ImportConnectionsDialog from "../components/issues/ImportConnectionsDialog";
import { fetchJiraIssues } from "../adapters/jiraAdapter";
import { fetchSlackAlerts } from "../adapters/slackAdapter";
import { saveImportedIssues, removeDuplicateIssues } from "../lib/issueImport";
import { useAuth } from "../contexts/AuthContext";
import { issuesService, batchDataChanges } from "../services/database";
import { useToast, ToastContainer } from "../components/ui/toast";
import { ImportProgress } from "../components/ui/progress";
import { runJob } from "../lib/backgroundJobs";
import { postIssueToSlack, getSlackSyncConfig } from "../services/slackSync";
import { integrationsEnabled, getIntegrationStatus, getIntegrationSettings, buildJiraJql, listSlackChannels } from "../lib/integrations";

const TIERS = {
  tier1: { label: "Tier 1 (Critical)", short: "Tier 1 · critical", colorBg: "var(--danger-soft)", color: "var(--danger)" },
  tier2: { label: "Tier 2 (Urgent)", short: "Tier 2 · urgent", colorBg: "var(--warning-soft)", color: "var(--warning)" },
  noise: { label: "Noise", short: "Noise", colorBg: "var(--bg-subtle)", color: "var(--fg-subtle)" },
};

const STATUS = ["new", "triaged", "resolved"];

const SEED_ISSUES = [
  {
    id: "IS-101",
    title: "Spike in checkout failures (HTTP 500)",
    source: "Backend/Alerts",
    tier: "tier1",
    status: "new",
    owner: "",
    created_date: new Date().toISOString(),
    updated_date: new Date().toISOString(),
    tags: ["checkout", "payments", "prod"],
    related_problem_id: "12",
    summary: "Error rate over SLO threshold in /orders/confirm for 18 minutes.",
    impact: 5,
  },
  {
    id: "IS-102",
    title: "App crashes on Android 14 when opening camera",
    source: "Crashlytics",
    tier: "tier2",
    status: "new",
    owner: "Mobile Team",
    created_date: new Date().toISOString(),
    updated_date: new Date().toISOString(),
    tags: ["android", "camera", "crash"],
    related_problem_id: "",
    summary: "Null pointer in CameraProvider after permission revocation.",
    impact: 3,
  },
  {
    id: "IS-103",
    title: "Spam leads coming from Leadbox legacy URL",
    source: "Support",
    tier: "tier1",
    status: "triaged",
    owner: "Growth",
    created_date: new Date().toISOString(),
    updated_date: new Date().toISOString(),
    tags: ["fraud", "leadbox"],
    related_problem_id: "5",
    summary: "Multiple fraudulent submissions, need auto-refer + rotate channel id.",
    impact: 4,
  },
  {
    id: "IS-104",
    title: "Slow page: /analytics/overview (>4s TTFB)",
    source: "Web Vitals",
    tier: "tier2",
    status: "new",
    owner: "",
    created_date: new Date().toISOString(),
    updated_date: new Date().toISOString(),
    tags: ["performance"],
    related_problem_id: "",
    summary: "Regressed after last deployment; likely N+1 in dashboard queries.",
    impact: 2,
  },
  {
    id: "IS-105",
    title: "Noisy alert: CPU 75% on staging",
    source: "Infra",
    tier: "noise",
    status: "new",
    owner: "",
    created_date: new Date().toISOString(),
    updated_date: new Date().toISOString(),
    tags: ["staging", "infra"],
    related_problem_id: "",
    summary: "Staging node autoscaler expected to handle spike; consider lowering sensitivity.",
    impact: 1,
  },
];




export default function Firefighter() {
  const [issues, setIssues] = useState([]);
  const [search, setSearch] = useState("");
  const [tierFilter, setTierFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("open");
  const { user, loading } = useAuth();
  const { toasts, showSuccess, showInfo, showError, removeToast } = useToast();

  // Import progress state
  const [importProgress, setImportProgress] = useState({
    isImporting: false,
    currentCount: 0,
    totalExpected: null,
    operation: "Importing"
  });

  // Load user-specific issues from Supabase
  const loadIssues = useCallback(async () => {
    // Don't load if still authenticating or no user
    if (loading || !user?.id) {
      if (!loading && !user?.id) {
        console.log('🚫 loadIssues: Auth completed but no user ID available');
        setIssues([]);
      }
      return;
    }

    console.log('🔄 loadIssues: Loading data for user:', user.id);

    try {
      let userIssues = await issuesService.list("-updated_date", 999, { user_id: user.id });
      // Older imports stored a new copy of a Jira issue on every import; keep one per key.
      const removed = await removeDuplicateIssues(userIssues);
      if (removed.length) {
        const gone = new Set(removed);
        userIssues = userIssues.filter((i) => !gone.has(i.id));
        console.log(`🧹 loadIssues: removed ${removed.length} duplicate Jira issue copies`);
      }
      console.log('✅ loadIssues: Loaded user issues from Supabase:', userIssues.length);
      console.log('📋 loadIssues: Issues sample:', userIssues.slice(0, 3));
      setIssues(userIssues);
    } catch (error) {
      console.error('❌ loadIssues: Error loading Firefighter data:', error);
      setIssues([]);
    }
  }, [user?.id, loading]);

  useEffect(() => {
    loadIssues();
    window.addEventListener('spcc:autopilot-updated', loadIssues);
    return () => window.removeEventListener('spcc:autopilot-updated', loadIssues);
  }, [loadIssues]);

  // Auto-import from JIRA if connected (once per day)
  useEffect(() => {
    const autoImportJira = async () => {
      if (loading || !user?.id) return;

      try {
        if (!integrationsEnabled) return;
        const [status, settings] = await Promise.all([getIntegrationStatus(), getIntegrationSettings()]);
        if (!status.jira) {
          console.log('🚫 Auto-import JIRA: Jira not connected');
          return;
        }
        const jiraConfig = settings.jira;

        // Check if we've auto-imported recently (within 24 hours)
        const lastAutoImport = localStorage.getItem(`spcc:last-auto-import-jira:${user.id}`);
        const now = Date.now();
        const twentyFourHours = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

        if (lastAutoImport && (now - parseInt(lastAutoImport)) < twentyFourHours) {
          console.log('🕐 Auto-import JIRA: Recently imported, skipping');
          return;
        }

        console.log('🔄 Auto-import JIRA: Starting automatic import...');

        // Use the existing import function with the stored config
        const importCount = await importFromJira(jiraConfig);

        // Update last import timestamp
        localStorage.setItem(`spcc:last-auto-import-jira:${user.id}`, now.toString());

        console.log(`✅ Auto-import JIRA: Successfully imported ${importCount} issues`);

        // Show toast notification for successful auto-import
        if (importCount > 0) {
          showSuccess(
            `🎉 Auto-imported ${importCount} JIRA issue${importCount === 1 ? '' : 's'} in the background`,
            6000 // Show for 6 seconds
          );
          console.log(`🎉 Auto-imported ${importCount} new/updated JIRA issues`);
        } else {
          showInfo('✅ JIRA sync complete - no new issues found', 4000);
        }

      } catch (error) {
        console.error('❌ Auto-import JIRA failed:', error);
        // Don't show error to user for auto-import failures
      }
    };

    // Run auto-import after a short delay to let the component settle
    const timeoutId = setTimeout(autoImportJira, 2000);
    return () => clearTimeout(timeoutId);
  }, [user?.id, loading]);

  // Auto-import from Slack if connected (once per day)
  useEffect(() => {
    const autoImportSlack = async () => {
      if (loading || !user?.id) return;

      try {
        if (!integrationsEnabled) return;
        const [status, settings] = await Promise.all([getIntegrationStatus(), getIntegrationSettings()]);
        if (!status.slack || !settings.slack.channel) {
          console.log('🚫 Auto-import Slack: Slack not connected or no channel chosen');
          return;
        }
        const slackConfig = settings.slack;

        // Check if we've auto-imported recently (within 24 hours)
        const lastAutoImport = localStorage.getItem(`spcc:last-auto-import-slack:${user.id}`);
        const now = Date.now();
        const twentyFourHours = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

        if (lastAutoImport && (now - parseInt(lastAutoImport)) < twentyFourHours) {
          console.log('🕐 Auto-import Slack: Recently imported, skipping');
          return;
        }

        console.log('🔄 Auto-import Slack: Starting automatic import...');

        // Use the existing import function with the stored config
        const importCount = await importFromSlack(slackConfig);

        // Update last import timestamp
        localStorage.setItem(`spcc:last-auto-import-slack:${user.id}`, now.toString());

        console.log(`✅ Auto-import Slack: Successfully imported ${importCount} messages`);

        // Show toast notification for successful auto-import
        if (importCount > 0) {
          showSuccess(
            `🎉 Auto-imported ${importCount} Slack message${importCount === 1 ? '' : 's'} in the background`,
            6000 // Show for 6 seconds
          );
          console.log(`🎉 Auto-imported ${importCount} new/updated Slack messages`);
        } else {
          showInfo('✅ Slack sync complete - no new messages found', 4000);
        }

      } catch (error) {
        console.error('❌ Auto-import Slack failed:', error);
        // Don't show error to user for auto-import failures
      }
    };

    // Run auto-import after a short delay (slightly after JIRA to stagger the requests)
    const timeoutId = setTimeout(autoImportSlack, 3000);
    return () => clearTimeout(timeoutId);
  }, [user?.id, loading]);



  // New users start with clean data (no seed issues)



  // 🔌 added: import dialog state
  const [importOpen, setImportOpen] = useState(false);

  // Slack issues store a channel ID; cards show the channel's name instead.
  // Loaded once per user, and only when there are Slack issues to label.
  const [channelNames, setChannelNames] = useState(() => new Map());
  const hasSlackIssues = issues.some((issue) => slackChannelId(issue));
  useEffect(() => {
    if (loading || !user?.id || !integrationsEnabled || !hasSlackIssues) return;
    let alive = true;
    getIntegrationStatus()
      .then((status) => (status.slack ? listSlackChannels() : []))
      .then((channels) => { if (alive) setChannelNames(new Map(channels.map((c) => [c.id, c.name]))); })
      .catch((error) => console.warn("Could not load Slack channel names:", error.message));
    return () => { alive = false; };
  }, [user?.id, loading, hasSlackIssues]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return issues.filter((it) => {
      // Convert tier filter string to integer for comparison
      if (tierFilter !== "all") {
        const tierValue = tierFilter === "tier1" ? 1 : tierFilter === "tier2" ? 2 : tierFilter === "noise" ? 3 : null;
        if (tierValue && it.tier !== tierValue) return false;
      }
      if (statusFilter === "open" && it.status === "resolved") return false;
      if (statusFilter !== "open" && statusFilter !== "all" && it.status !== statusFilter) return false;
      if (!q) return true;
      const blob = [
        it.id,
        it.title,
        it.source,
        it.owner,
        ...(it.tags || []),
        it.summary || "",
        it.related_problem_id || "",
        channelNames.get(slackChannelId(it)) || "",
      ]
        .join(" ")
        .toLowerCase();
      return blob.includes(q);
    });
  }, [issues, search, tierFilter, statusFilter, channelNames]);

  const byTier = useMemo(
    () => ({
      tier1: filtered.filter((i) => i.tier === 1),
      tier2: filtered.filter((i) => i.tier === 2),
      noise: filtered.filter((i) => i.tier === 3),
    }),
    [filtered]
  );

  // On wide screens the tier lanes reach exactly to the bottom of the window and
  // scroll on their own, so the header, stats and filters never scroll away.
  // Narrower screens stack the lanes; there the CSS cap (70dvh) applies instead.
  const boardRef = useRef(null);
  const laneMaxHeight = useFitHeight(boardRef);

  // Stable so memoized IssueCards don't re-render when unrelated page state changes.
  const updateIssue = useCallback(async (id, patch) => {
    try {
      // Update in Supabase first
      await issuesService.update(id, { ...patch, updated_date: new Date().toISOString() });
      // Then update local state
      setIssues((prev) =>
        prev.map((i) =>
          i.id === id ? { ...i, ...patch, updated_date: new Date().toISOString() } : i
        )
      );
    } catch (error) {
      console.error('Error updating issue:', error);
      alert('Failed to update issue. Please try again.');
    }
  }, []);

  const [resolvingTier, setResolvingTier] = useState(null);

  const bulkResolveTier = async (tier) => {
    // Map tier names to integer values for filtering
    const tierValue = tier === 'tier1' ? 1 : tier === 'tier2' ? 2 : 3;
    const tierLabel = tier === 'noise' ? 'noise' : tier === 'tier2' ? 'Tier 2' : 'Tier 1';
    const issuesToUpdate = issues.filter(i => i.tier === tierValue && i.status !== "resolved");

    if (issuesToUpdate.length === 0) {
      showInfo(`No open ${tierLabel} issues to resolve.`, 4000);
      return;
    }

    setResolvingTier(tier);
    try {
      const resolvedIds = await runJob(`Resolving ${tierLabel}`, async (job) => {
        job.update({ done: 0, total: issuesToUpdate.length });
        // Get Slack config for bi-directional sync
        const slackConfig = await getSlackSyncConfig();
        const ids = [];

        // Update each issue individually since we need to set status, not tier
        for (const issue of issuesToUpdate) {
          try {
            await issuesService.update(issue.id, {
              status: 'resolved',
              updated_date: new Date().toISOString()
            });
            ids.push(issue.id);

            // Post update to Slack if issue originated from Slack
            if (slackConfig && issue.source === 'Slack') {
              await postIssueToSlack(issue, 'resolved', slackConfig);
              console.log(`✅ Posted resolution to Slack for issue ${issue.id}`);
            }
          } catch (error) {
            console.error(`Error resolving issue ${issue.id}:`, error);
          }
          job.update({ done: ids.length });
        }
        return ids;
      });

      // Update local state for the issues that were saved
      const resolved = new Set(resolvedIds);
      setIssues((prev) =>
        prev.map((i) =>
          resolved.has(i.id) ? { ...i, status: "resolved", updated_date: new Date().toISOString() } : i
        )
      );

      const failed = issuesToUpdate.length - resolvedIds.length;
      if (resolvedIds.length) showSuccess(`Resolved ${resolvedIds.length} ${tierLabel} issue${resolvedIds.length === 1 ? '' : 's'}`, 4000);
      if (failed) showError(`${failed} ${tierLabel} issue${failed === 1 ? '' : 's'} could not be resolved.`, 8000);
    } catch (error) {
      console.error('Error bulk resolving issues:', error);
      showError('Failed to resolve issues. Please try again.', 8000);
    } finally {
      setResolvingTier(null);
    }
  };

  const resetFilters = () => {
    setSearch("");
    setTierFilter("all");
    setStatusFilter("open");
  };


  const resetDemoData = async () => {
    if (!user?.id) return;

    try {
      console.log('Resetting demo data with seed issues...');

      // Clear current issues and create seed issues
      setIssues([]);

      // Create seed issues in Supabase
      for (const issue of SEED_ISSUES) {
        try {
          await issuesService.create({
            external_id: issue.id,
            title: issue.title,
            description: issue.summary || '',
            status: 'new',
            priority: issue.tier === 'tier1' ? 'critical' : issue.tier === 'tier2' ? 'high' : 'medium',
            severity: issue.tier === 'tier1' ? 'critical' : issue.tier === 'tier2' ? 'high' : 'medium',
            tier: issue.tier === 'tier1' ? 1 : issue.tier === 'tier2' ? 2 : 3,
            source: issue.source || 'manual',
            assignee: issue.owner || null,
            labels: issue.tags || [],
            created_date: issue.created_date,
            updated_date: issue.updated_date,
            triage_notes: `Impact: ${issue.impact || 'N/A'}`
          });
        } catch (error) {
          console.error('Error creating seed issue:', error);
        }
      }

      // Reload issues from database
      await loadIssues();
      console.log('Demo data reset complete');
    } catch (error) {
      console.error('Error resetting demo data:', error);
      alert('Failed to reset demo data. Please try again.');
    }
  };


  async function importFromJira(conf) {
    setImportProgress({ isImporting: true, currentCount: 0, totalExpected: null, operation: "Importing" });
    try {
      const rows = await fetchJiraIssues({ jql: buildJiraJql(conf), max: conf.max });
      console.log(`[JIRA] fetched ${rows.length} rows`);
      setImportProgress((p) => ({ ...p, totalExpected: rows.length || null }));

      // Re-render the progress overlay at most every 250ms, not once per issue.
      let lastTick = 0;
      const counts = await saveImportedIssues(rows, {
        onProgress: (done, total) => {
          const now = Date.now();
          if (done !== total && now - lastTick < 250) return;
          lastTick = now;
          setImportProgress((p) => ({ ...p, currentCount: done, totalExpected: total }));
        },
      });
      await loadIssues();

      if (rows.length === 0) {
        showInfo("JIRA search returned 0 issues. Check your spaces and JQL filter.", 6000);
      } else {
        const parts = [`${counts.created} new`, `${counts.updated} updated`];
        if (counts.unchanged) parts.push(`${counts.unchanged} unchanged`);
        showSuccess(`✅ Imported ${rows.length} JIRA issue${rows.length === 1 ? '' : 's'} (${parts.join(', ')})`, 5000);
        if (counts.failed) showError(`${counts.failed} JIRA issue${counts.failed === 1 ? '' : 's'} could not be saved.`, 8000);
      }
      return rows.length;
    } catch (err) {
      console.error(err);
      showError(`JIRA import failed: ${err.message}`, 8000);
      return 0;
    } finally {
      setImportProgress({ isImporting: false, currentCount: 0, totalExpected: null, operation: "Importing" });
    }
  }

  async function importFromSlack(conf) {
    try {
      console.log('[SLACK] Starting import with config:', conf);
      const rows = await runJob('Importing from Slack', async (job) => {
        const fetched = await fetchSlackAlerts(conf);
        console.log(`[SLACK] Fetched ${fetched.length} messages from Slack`);
        job.update({ done: 0, total: fetched.length || null });

        // Save each message to database
        let saved = 0;
        await batchDataChanges(async () => {
          for (const row of fetched) {
            await issuesService.create(row);
            job.update({ done: ++saved });
          }
        });

        // Reload issues from database to get fresh data
        await loadIssues();
        return fetched;
      });
      console.log(`[SLACK] Saved ${rows.length} issues to database and reloaded`);

      if (rows.length === 0) {
        showInfo("No new Slack messages found in the lookback period.", 5000);
      } else {
        showSuccess(
          `✅ Successfully imported ${rows.length} Slack message${rows.length === 1 ? '' : 's'}!`,
          5000
        );
      }
      return rows.length;
    } catch (err) {
      console.error('[SLACK] Import failed:', err);
      showError(`Slack import failed: ${err.message}`, 8000);
      return 0;
    }
  }

  return (
    <div>
      <PageHeader
        icon={ShieldAlert}
        eyebrow="Operate"
        title="Firefighter"
        description="Triage production issues and alerts. Auto-tier, delegate, resolve."
        actions={<>
          <Button variant="secondary" disabled={!!resolvingTier} onClick={() => bulkResolveTier("noise")}>Resolve all noise</Button>
          <Button variant="secondary" disabled={!!resolvingTier} onClick={() => bulkResolveTier("tier2")}>Resolve all Tier 2</Button>
          <Button variant="primary" onClick={() => setImportOpen(true)} title="Connect & import from Jira/Slack">
            <CloudDownload size={15} />
            Import
          </Button>
        </>}
      />

      <StatGrid>
        {Object.entries(byTier).map(([k, arr]) => (
          <Stat key={k} label={TIERS[k].short} value={arr.length} tone={k === "tier1" && arr.length ? "danger" : undefined} />
        ))}
        <Stat label="Showing" value={filtered.length} hint={filtered.length === 1 ? "issue" : "issues"} />
      </StatGrid>
      <WorkspaceInsights area="firefighter"/>

      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 12 }}>
        <div style={{ flex: 1, minWidth: 220, maxWidth: 380 }}>
          <Input
            type="search"
            aria-label="Search issues"
            placeholder="Search id, title, source, owner, tags…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <Select value={tierFilter} onValueChange={setTierFilter} style={{ width: 170 }}>
          <SelectTrigger aria-label="Tier filter">
            <SelectValue placeholder="Tier" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All tiers</SelectItem>
            <SelectItem value="tier1">Tier 1 (Critical)</SelectItem>
            <SelectItem value="tier2">Tier 2 (Urgent)</SelectItem>
            <SelectItem value="noise">Noise</SelectItem>
          </SelectContent>
        </Select>

        <Select value={statusFilter} onValueChange={setStatusFilter} style={{ width: 170 }}>
          <SelectTrigger aria-label="Status filter">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="open">Open (not resolved)</SelectItem>
            <SelectItem value="all">All</SelectItem>
            {STATUS.map((s) => (
              <SelectItem key={s} value={s}>
                {s}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Button variant="ghost" onClick={resetFilters}>
          <RefreshCw size={14} />
          Reset
        </Button>
      </div>

      <div ref={boardRef} className="grid cols-3" style={{ alignItems: "start" }}>
        {["tier1", "tier2", "noise"].map((tierKey) => {
          const items = byTier[tierKey] || [];
          const pal = TIERS[tierKey];
          return (
            <section key={tierKey} className="board-lane lane-scroll" aria-label={pal.label} style={laneMaxHeight ? { maxHeight: laneMaxHeight } : undefined}>
              <header className="board-lane-header">
                <span className="lane-dot" style={{ background: pal.color }} aria-hidden="true" />
                <h2>{pal.label}</h2>
                <span className="tabular">{items.length}</span>
              </header>
              {items.length === 0 ? (
                <div className="board-lane-empty">
                  <Inbox size={14} style={{ verticalAlign: "-2px", marginRight: 6 }} />
                  Nothing here
                </div>
              ) : (
                <div className="board-lane-body" tabIndex={0} aria-label={`${pal.label} issues`}>
                  {items.map((it) => (
                    <IssueCard
                      key={it.id}
                      issue={it}
                      onUpdate={updateIssue}
                      channelNames={channelNames}
                    />
                  ))}
                </div>
              )}
            </section>
          );
        })}
      </div>

      {/* 🔌 Import dialog */}
      <ImportConnectionsDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImportJira={importFromJira}
        onImportSlack={importFromSlack}
      />

      {/* Toast notifications */}
      <ToastContainer toasts={toasts} removeToast={removeToast} />

      {/* Import progress overlay */}
      <ImportProgress
        isImporting={importProgress.isImporting}
        currentCount={importProgress.currentCount}
        totalExpected={importProgress.totalExpected}
        operation={importProgress.operation}
      />
    </div>
  );
}
